import { useEffect, useState } from "react";
import { useGameStore, SCREEN } from "@/stores/gameStore";
import { PROFILE } from "@/constants/testIds";
import { fetchProfile, updateProfile, fetchMatches } from "@/lib/api";
import { toast } from "sonner";
import LobbyLayout from "@/components/LobbyLayout";
import { X, Save, Trophy, Target, Skull, Percent } from "lucide-react";

export default function ProfileScreen() {
    const goto = useGameStore((s) => s.goto);
    const [profile, setProfile] = useState(null);
    const [name, setName] = useState("");
    const [avatar, setAvatar] = useState("#00e5ff");
    const [matches, setMatches] = useState([]);

    useEffect(() => {
        fetchProfile()
            .then((p) => {
                setProfile(p);
                setName(p.name);
                setAvatar(p.avatar_color);
            })
            .catch(() => {});
        fetchMatches(6).then(setMatches).catch(() => {});
    }, []);

    const save = async () => {
        try {
            const p = await updateProfile({ name, avatar_color: avatar });
            setProfile(p);
            toast.success("Profile updated");
        } catch (e) {
            toast.error("Update failed");
        }
    };

    if (!profile) {
        return (
            <div className="fixed inset-0 bg-black flex items-center justify-center">
                <div className="axis-mono text-cyan-300 text-sm tracking-[0.5em] animate-pulse">
                    LOADING PROFILE...
                </div>
            </div>
        );
    }

    const kd = profile.deaths ? (profile.kills / profile.deaths).toFixed(2) : profile.kills.toFixed(2);
    const winRate = profile.matches_played ? Math.round((profile.wins / profile.matches_played) * 100) : 0;
    const xpToNext = 500 - (profile.xp % 500);
    const progress = ((profile.xp % 500) / 500) * 100;

    return (
        <LobbyLayout active={SCREEN.PROFILE}>
        <div data-testid={PROFILE.root} className="w-full h-full text-white flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-8 py-4">
                <div>
                    <div className="axis-mono text-[10px] tracking-[0.5em] text-orange-300/80">OPERATOR</div>
                    <div className="axis-heading text-3xl tracking-[0.4em]">PROFILE</div>
                </div>
                <button
                    data-testid={PROFILE.close}
                    onClick={() => goto(SCREEN.MENU)}
                    className="axis-btn axis-btn-ghost axis-cut-sm px-4 py-2 text-xs flex items-center gap-2"
                >
                    <X size={14} /> Close
                </button>
            </div>

            <div className="flex-1 overflow-y-auto px-10 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                    {/* Card */}
                    <div className="axis-glass axis-cut p-6">
                        <div
                            className="w-24 h-24 axis-cut-sm border-2 flex items-center justify-center mx-auto mb-4"
                            style={{ borderColor: avatar, background: `${avatar}22`, color: avatar }}
                        >
                            <span className="axis-heading text-4xl">{name.charAt(0).toUpperCase() || "A"}</span>
                        </div>

                        <div className="mb-4">
                            <label className="axis-mono text-[10px] tracking-[0.4em] text-white/50">CALLSIGN</label>
                            <input
                                data-testid={PROFILE.name}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                maxLength={20}
                                className="mt-1 w-full bg-black/60 border border-white/20 axis-cut-sm px-3 py-2 text-white axis-heading tracking-widest focus:border-cyan-400 focus:outline-none"
                            />
                        </div>

                        <div className="mb-4">
                            <label className="axis-mono text-[10px] tracking-[0.4em] text-white/50">AVATAR COLOR</label>
                            <div className="flex gap-2 mt-2">
                                {["#00e5ff", "#00ff66", "#ffb300", "#ff2a2a", "#c084fc", "#ffffff"].map((c) => (
                                    <button
                                        key={c}
                                        onClick={() => setAvatar(c)}
                                        style={{ background: c, borderColor: avatar === c ? "#fff" : "transparent" }}
                                        className="w-8 h-8 axis-cut-sm border-2"
                                    />
                                ))}
                            </div>
                        </div>

                        <button
                            data-testid={PROFILE.save}
                            onClick={save}
                            className="axis-btn axis-btn-primary axis-cut-sm w-full py-2 text-sm flex items-center justify-center gap-2"
                        >
                            <Save size={14} /> Save Changes
                        </button>
                    </div>

                    {/* Stats */}
                    <div className="lg:col-span-2 axis-glass axis-cut p-6" data-testid={PROFILE.stats}>
                        <div className="flex items-center justify-between mb-6">
                            <div className="axis-heading text-lg tracking-widest">CAREER STATISTICS</div>
                            <div className="axis-mono text-[10px] tracking-[0.4em] text-cyan-300/80">
                                LVL {profile.level} · {profile.xp} XP
                            </div>
                        </div>

                        {/* Level progress */}
                        <div className="mb-6">
                            <div className="h-2 bg-white/5 axis-cut-sm border border-white/10 overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-cyan-500 to-cyan-300 axis-glow"
                                    style={{ width: `${progress}%`, transition: "width 0.6s ease" }}
                                />
                            </div>
                            <div className="axis-mono text-[10px] tracking-[0.3em] text-white/50 mt-1">
                                {xpToNext} XP TO NEXT LEVEL
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <StatCard icon={Target} label="Kills" value={profile.kills} color="#00e5ff" />
                            <StatCard icon={Skull} label="Deaths" value={profile.deaths} color="#ff2a2a" />
                            <StatCard icon={Trophy} label="Wins" value={profile.wins} color="#00ff66" />
                            <StatCard icon={Percent} label="Win Rate" value={`${winRate}%`} color="#ffb300" />
                            <StatCard label="Matches" value={profile.matches_played} />
                            <StatCard label="Losses" value={profile.losses} />
                            <StatCard label="K/D" value={kd} accent />
                            <StatCard label="XP Total" value={profile.xp} />
                        </div>

                        <div className="mt-6">
                            <div className="axis-mono text-[10px] tracking-[0.4em] text-white/50 mb-2">RECENT MATCHES</div>
                            {matches.length === 0 ? (
                                <div className="axis-mono text-xs text-white/40 py-4">No matches yet. Deploy to record your first game.</div>
                            ) : (
                                <div className="space-y-1.5">
                                    {matches.map((m) => (
                                        <div key={m.id} className="flex items-center justify-between axis-cut-sm border border-white/5 px-3 py-2 text-xs">
                                            <span className={`axis-heading tracking-widest ${m.won ? "text-cyan-300" : "text-red-400"}`}>
                                                {m.won ? "VICTORY" : "DEFEAT"}
                                            </span>
                                            <span className="axis-mono text-white/60">{m.map} · {m.team_size} · {m.difficulty}</span>
                                            <span className="axis-mono text-white/80">{m.kills}K / {m.deaths}D</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </LobbyLayout>
    );
}

function StatCard({ icon: Icon, label, value, color, accent }) {
    return (
        <div className="axis-cut-sm border border-white/10 bg-white/[0.02] p-3">
            <div className="flex items-center gap-2 mb-1">
                {Icon && <Icon size={12} style={{ color: color || "#8c92a0" }} />}
                <span className="axis-mono text-[10px] tracking-[0.3em] text-white/50">{label}</span>
            </div>
            <div
                className={`axis-hud text-3xl ${accent ? "text-cyan-300" : "text-white"}`}
                style={color ? { color } : {}}
            >
                {value}
            </div>
        </div>
    );
}
